// Platform Admin Portal — API helpers (Prompt 8).
import { api, API } from "./api";

export async function getAdminDashboard(userId, { period } = {}) {
  const params = { user_id: userId };
  if (period) params.period = period;
  const { data } = await api.get("/admin-portal/dashboard", { params });
  return data;
}

export async function listAdminUsers(userId, { role, q } = {}) {
  const params = { user_id: userId };
  if (role && role !== "all") params.role = role;
  if (q) params.q = q;
  const { data } = await api.get("/admin-portal/users", { params });
  return data;
}

export async function getAdminUser(targetId, userId) {
  const { data } = await api.get(
    `/admin-portal/users/${encodeURIComponent(targetId)}`,
    { params: { user_id: userId } },
  );
  return data;
}

export async function deactivateUser(targetId, userId, reason) {
  const { data } = await api.post(
    `/admin-portal/users/${encodeURIComponent(targetId)}/deactivate`,
    { actor_user_id: userId, reason },
  );
  return data;
}

export async function reactivateUser(targetId, userId, note) {
  const { data } = await api.post(
    `/admin-portal/users/${encodeURIComponent(targetId)}/reactivate`,
    { actor_user_id: userId, note: note || "" },
  );
  return data;
}

export async function listAdminArchitects(userId, { status, q } = {}) {
  const params = { user_id: userId };
  if (status && status !== "all") params.status = status;
  if (q) params.q = q;
  const { data } = await api.get("/admin-portal/architects", { params });
  return data;
}

export async function getAdminArchitect(architectId, userId) {
  const { data } = await api.get(
    `/admin-portal/architects/${encodeURIComponent(architectId)}`,
    { params: { user_id: userId } },
  );
  return data;
}

export async function publishArchitectProfile(architectId, userId, reason) {
  const { data } = await api.post(
    `/admin-portal/architects/${encodeURIComponent(architectId)}/publish-profile`,
    { actor_user_id: userId, reason: reason || "" },
  );
  return data;
}

export async function unpublishArchitectProfile(architectId, userId, reason) {
  const { data } = await api.post(
    `/admin-portal/architects/${encodeURIComponent(architectId)}/unpublish-profile`,
    { actor_user_id: userId, reason },
  );
  return data;
}

export async function adminEditArchitectProfile(architectId, userId, reason, fields) {
  const { data } = await api.patch(
    `/admin-portal/architects/${encodeURIComponent(architectId)}/profile`,
    { actor_user_id: userId, reason, ...fields },
  );
  return data;
}

export async function approveVerification(architectId, userId, note) {
  const { data } = await api.post(
    `/admin-portal/architects/${encodeURIComponent(architectId)}/verification/approve`,
    { actor_user_id: userId, note: note || "" },
  );
  return data;
}

export async function rejectVerification(architectId, userId, note) {
  const { data } = await api.post(
    `/admin-portal/architects/${encodeURIComponent(architectId)}/verification/reject`,
    { actor_user_id: userId, note },
  );
  return data;
}

export async function startImpersonation(targetUserId, actorUserId, reason) {
  const { data } = await api.post("/admin-portal/impersonate/start", {
    actor_user_id: actorUserId,
    target_user_id: targetUserId,
    reason: reason || "",
  });
  return data;
}

export async function stopImpersonation(targetUserId, actorUserId) {
  const { data } = await api.post("/admin-portal/impersonate/stop", {
    actor_user_id: actorUserId,
    target_user_id: targetUserId,
  });
  return data;
}

export async function listCodeChanges(userId, { period } = {}) {
  const params = { user_id: userId };
  if (period) params.period = period;
  const { data } = await api.get("/admin-portal/code-changes", { params });
  return data;
}

export async function getCodeChange(ccId, userId) {
  const { data } = await api.get(
    `/admin-portal/code-changes/${encodeURIComponent(ccId)}`,
    { params: { user_id: userId } },
  );
  return data;
}

export async function createCodeChange(userId, body) {
  const { data } = await api.post("/admin-portal/code-changes", {
    actor_user_id: userId,
    ...body,
  });
  return data;
}

export async function removeCodeChange(ccId, userId, reason) {
  const { data } = await api.post(
    `/admin-portal/code-changes/${encodeURIComponent(ccId)}/remove`,
    { actor_user_id: userId, reason },
  );
  return data;
}

export async function runReport(kind, userId, params = {}) {
  const merged = { user_id: userId, ...params };
  const { data } = await api.get(
    `/admin-portal/reports/${encodeURIComponent(kind)}`,
    { params: merged },
  );
  return data;
}

export async function getAdminSettings(userId) {
  const { data } = await api.get("/admin-portal/settings", {
    params: { user_id: userId },
  });
  return data;
}

export async function getAdminAudit(userId, filters = {}) {
  const params = { user_id: userId };
  if (filters.collection && filters.collection !== "all")
    params.collection = filters.collection;
  if (filters.types?.length) params.types = filters.types.join(",");
  if (filters.actor && filters.actor !== "all") params.actor = filters.actor;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  const { data } = await api.get("/admin-portal/audit", { params });
  return data;
}

export function adminExportUrl(kind, userId, extra = {}) {
  const usp = new URLSearchParams({ user_id: userId });
  Object.entries(extra).forEach(([k, v]) => {
    if (v == null || v === "") return;
    if (Array.isArray(v)) v = v.join(",");
    usp.set(k, String(v));
  });
  return `${API}/admin-portal/exports/${kind}.csv?${usp.toString()}`;
}
